import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HowWeHelpSection = ({ headerData, serviceCards, statsDataHeading, statsData, worldMapImg }) => {
  const sectionRef = useRef(null);
  const headerTitleRef = useRef(null);
  const headerHighlightedTextRef = useRef(null);
  const headerNormalTextRef = useRef(null);
  const headerDescriptionRef = useRef(null);
  const headerImgRef = useRef(null);
  const serviceCardRefs = useRef([]);
  const statsHeadingRef = useRef(null);
  const statBlockRefs = useRef([]);
  const worldMapImgRef = useRef(null);
  const animationTriggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationTriggered.current) {
            animationTriggered.current = true;
            animateSection();
            if (sectionRef.current) {
              observer.unobserve(sectionRef.current);
            }
          }
        });
      },
      {
        threshold: 0,
      }
    );

    const currentRef = sectionRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animateSection = () => {
    const tl = gsap.timeline({ defaults: { duration: 0.5, ease: "power2.out" } });

    // Header Section Animation
    tl.fromTo(headerTitleRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "+=0.1");
    tl.fromTo(headerHighlightedTextRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "-=0.4");
    tl.fromTo(headerNormalTextRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "-=0.4");
    tl.fromTo(headerDescriptionRef.current, { yPercent: 20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5 }, "-=0.4");
    tl.fromTo(headerImgRef.current, { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.5 }, "-=0.6");

    // Service Cards Grid Animation
    tl.fromTo(
      serviceCardRefs.current,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.08, duration: 0.4 },
      "+=0.2"
    );

    // Why Work With Us Section Animation
    tl.fromTo(statsHeadingRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "+=0.2");
    tl.fromTo(
      statBlockRefs.current,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.1, duration: 0.4 },
      "+=0.1"
    );
    tl.fromTo(worldMapImgRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5 }, "+=0.1");
  };

  return (
    <div className="md:pt-16 pt-8" ref={sectionRef}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-center justify-between md:mb-20 mb-12">
        <div className="mb-8 md:mb-0 flex flex-col justify-center items-center px-5 min-[640px]:px-14 gap-y-4">
          <h2 className="text-[34px] sm:text-[40px] font-[600] text-center leading-[34px] sm:leading-[38px] text-[#111827] sm:mb-2" ref={headerTitleRef}>{headerData.title}</h2>
          <h1 className="text-[34px] sm:text-[40px] font-[700] text-center leading-[34px] sm:leading-[38px] mb-4">
            <span className="text-[#111827]" ref={headerHighlightedTextRef}>{headerData.highlightedText}</span>
          </h1>
          <p className="text-[#4B5563] font-[600] text-[18px] text-center" ref={headerDescriptionRef}>
            {headerData.description}
          </p>
        </div>
      </div>

      {/* Service Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-1 gap-4 md:mb-16 mb-12">
        {serviceCards.map((card, index) => {
          const bgImage = index % 4 === 0 ? '/img/bgd1.png' :
            index % 4 === 1 ? '/img/bgd2.png' :
              index % 4 === 2 ? '/img/bgd2.png' : '/img/bgd1.png';

          return (
            <div
              key={index}
              ref={(el) => (serviceCardRefs.current[index] = el)}
              className="md:p-20 p-8 h-96 relative overflow-hidden text-white flex flex-col md:flex-row items-center justify-center rounded-lg"
              style={{
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center right'
              }}
            >
              <div className="flex w-full flex-col md:flex-row relative z-10 md:items-start"> 
                <div className="text-left md:pr-4 mb-4 md:mb-0">
                  <h3 className="text-[#FFFFFF] text-[34px] sm:text-[40px] leading-[50px] sm:leading-[60px] font-[700] mb-6">{card.title}</h3>
                  <p className="md:mb-6 foont-[500] text-[17px] sm:text-[20px] text-[#FFFFFF]">{card.text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HowWeHelpSection;